import React, { useContext, useEffect, useState } from "react";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { BreadCrumbs, Button, ConfirmationDialog, Pagination, PatientRequestForm, UserSideBar } from "../../components";
import { TableHeader, TableRow } from "../../components/TableComponents/TableComponents";
import { getPatientServiceList } from "../../redux/slices/PatientSlice";
import { useParams } from "react-router-dom";
import { BreadCrumbsType } from "../../components/BreadCrumbs/BreadCrumbs";
import moment from "moment";
import { getUserRequests } from "../../redux/slices/UserSlice";
import { useNavigate } from "react-router-dom";
import { getSorting, patientRenderState, renderState } from "../../utils";
import { headers } from "./PatientDashboardData";
import { ServiceBilledTo } from "../../models/service.model";
import { sortObj } from "../../redux/slices/OfficeSlice";
import { RefundRequestState, RequestType } from "../../models/request.model";

const PatientRequests = () => {
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [sortKeys] = useState(headers.map(head => head.sortKey));
  const [extraClasses] = useState(headers.map(head => head.extraClasses));
  const [sortMethods, setSortMethods] = useState<boolean[]>(headers.map(() => false));
  const [activeSortIndex, setActiveSortIndex] = useState<number>(-1);
  const [showAddOfficeSidebar, setShowAddOfficeSidebar] = useState(false);
  const [tableState, setTableState] = useState({
    start: 0,
    end: 10,
    pageNumber: 0,
    rowsPerPage: 10,
    sortedBy: "createdDate,desc",
  });
  const [showCloseAlert, setShowCloseAlert] = useState(false);

  const { errorMessage, toastVisible, showCurrentSubscription } = useSelector((state: RootState) => state.patient);
  const { userRequest } = useSelector((state: RootState) => state.user);
  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);
  const loading = false;
  const office = currentUser?.offices?.[0];
  const { patientId } = useParams();

  const breadcrumbs: BreadCrumbsType[] = [
    {
      title: "Requests",
      key: "#",
    },
  ];

  const updateTable = (sortObj?: sortObj) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? tableState.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? tableState.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? tableState.sortedBy;
    if (currentUser?.userId)
      dispatch(getUserRequests({ userId: currentUser?.userId, page: pageNumToBeCalled, size: sizeToBeCalled, sort: sortBy })).then(() => {
        setTableState({ ...tableState, pageNumber: pageNumToBeCalled, rowsPerPage: sizeToBeCalled, sortedBy: sortBy });
      });
  };

  const handleRowClick = (id: string) => {
    navigate(`/patients/${patientId}/requests/${id}`);
  };

  const handleSort = (index: number) => {
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    setActiveSortIndex(index);
    updateTable({ sortedBy: sortMethodString, pageNumber: 0, rowsPerPage: tableState.rowsPerPage });
  };

  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    updateTable({ sortedBy: tableState.sortedBy, pageNumber: newPage, rowsPerPage: tableState.rowsPerPage });
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTableState({ ...tableState, rowsPerPage: parseInt(event.target.value, 10) });
    updateTable({ sortedBy: tableState.sortedBy, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) });
  };

  const renderCloseMessage = () => {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="font-bold">{office?.name} is currently closed</h2>
        <p>{office?.tempClosedReason ?? ""}</p>
        <p>Your request will not be reviewed until the office has reopened.</p>
      </div>
    );
  };

  const navigateRequest = () => navigate(`/patients/${patientId}/requests/create-request`);

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;
  const sortIcons = sortMethods.map((item, index) => (activeSortIndex === index ? (item ? headerIconAsc : headerIconDesc) : null));

  const RenderTitleSubTitle = ({ title }: { title: string }) => {
    return (
      <>
        <span>{title}</span>
      </>
    );
  };

  useEffect(() => {
    dispatch(getPatientServiceList());
  }, []);

  useEffect(() => {
    if (currentUser?.userId) {
      updateTable();
    }
  }, [currentUser?.userId]);

  useEffect(() => {
    if (toastVisible) {
      toast?.openToast(errorMessage);
    }
  }, [toastVisible]);

  return (
    <>
      <main>
        <div className="container mx-auto p-4 md:p-8 min-h-[80vh]">
          <BreadCrumbs items={breadcrumbs} />
          <div className="md:flex justify-between items-center mb-4 md:mb-6">
            <h2 className="heading uppercase">Hi {currentUser?.firstName}</h2>
            <Button onClick={() => (office?.tempClosed ? setShowCloseAlert(true) : navigateRequest())} text={"Request a Service"} className="w-fit" />
          </div>

          <p className="text-base mb-2 md:mb-5 font-medium">You will find all your current and past form requests below. </p>

          {loading === false && userRequest.data !== undefined && userRequest.data !== null && userRequest.data.content.length ? (
            <div className="block">
              <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
                {headers.map((head, index) => (
                  <TableHeader
                    title={head.title}
                    index={index}
                    key={index}
                    sortIcon={sortIcons[index]}
                    handleSort={handleSort}
                    extraClassesHeader={extraClasses[index]}
                    isClickable={head.isClickable}
                  />
                ))}
              </div>
              {userRequest.data.content.map((rowData: any, index: number) => {
                const sortedData = {
                  request: rowData.requestNumber ? rowData.requestNumber : "-",
                  service: rowData?.service?.name ? <RenderTitleSubTitle title={rowData.service.name} /> : "-",
                  provider: rowData?.primaryCareProvider?.firstName
                    ? `Dr. ${rowData.primaryCareProvider.firstName} ${rowData.primaryCareProvider.lastName}`
                    : "-",
                  status:
                    rowData?.refundRequest?.refundRequestState === RefundRequestState.Pending ? (
                      <span className={`text-docsigna-orange`}>Refund Requested</span>
                    ) : (rowData?.serviceBilledToOverride || rowData?.service?.serviceBilledTo) === ServiceBilledTo.Patient ||
                      rowData?.requestType === RequestType.DocnoteDebit ? (
                      rowData?.state ? (
                        renderState[rowData.state]
                      ) : (
                        "-"
                      )
                    ) : rowData?.state ? (
                      patientRenderState(rowData?.state)
                    ) : (
                      "-"
                    ),
                  created: moment(rowData.createdDate).format("ll"),
                  comment: (
                    <>
                      <span> {rowData.numMessages ? rowData.numMessages : "-"}</span>
                      {rowData.numUnreadMessages ? (
                        <span className="text-xs m-2 text-green-700 font-medium py-1 px-2 bg-green-100 rounded-full">
                          {rowData.numUnreadMessages} New
                        </span>
                      ) : null}
                    </>
                  ),
                };
                return (
                  <TableRow
                    handleCloseTooltip={() => {}}
                    handleOpenTooltip={() => {}}
                    tooltipVisible={false}
                    data={Object.values(sortedData)}
                    key={index}
                    id={index.toString()}
                    extraClassesRow={extraClasses}
                    // tooltipOptions={[""]}
                    handleTooltipClick={() => {
                      toast?.openToast();
                    }}
                    onRowClick={() => handleRowClick(rowData.requestId)}
                  />
                );
              })}
              <Pagination
                colSpan={10}
                count={userRequest.data ? userRequest.data.totalElements : 0}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                page={tableState.pageNumber}
                rowsPerPage={tableState.rowsPerPage}
                // rowsPerPageOptions={[10, 25, 50, 100].filter(i => userRequest.data && i < userRequest.data?.totalElements)}
              />
            </div>
          ) : loading ? (
            <h2>Loading ...</h2>
          ) : (
            <p className="font-semibold">You have no requests at this time</p>
          )}
          <div></div>
        </div>
        <UserSideBar
          visible={showAddOfficeSidebar}
          LeftbuttonText="Cancel"
          RightbuttonText="Add"
          onCancelClick={() => setShowAddOfficeSidebar(false)}
          onInviteClick={() => setShowAddOfficeSidebar(false)}
          onClickOutside={() => setShowAddOfficeSidebar(false)}
          title="Submit a Request"
        >
          <PatientRequestForm onClickCancel={() => setShowAddOfficeSidebar(false)} />
        </UserSideBar>

        <ConfirmationDialog
          open={showCloseAlert}
          description={renderCloseMessage()}
          handleFailure={() => setShowCloseAlert(false)}
          handleSuccess={() => navigateRequest()}
          successButtonText="Proceed"
          failureButtonText="Cancel"
        />
      </main>
    </>
  );
};

export default PatientRequests;
